import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { FileArchive, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, fmtDate, PLATFORM_INFO } from "@/lib/api";
import { usePanel } from "./PanelContext";

function Toggle({ active, until, onOn, onOff, testId }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`text-xs font-semibold ${active ? "text-emerald-600" : "text-slate-400"}`}>{active ? fmtDate(until) : "Kapalı"}</span>
      <Button size="sm" className="rounded-full h-7 px-3 bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={onOn} data-testid={`${testId}-activate`}>{active ? "+1 Yıl" : "1 Yıl Aç"}</Button>
      {active && <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={onOff} data-testid={`${testId}-deactivate`}>Kapat</Button>}
    </div>
  );
}

export default function Admin() {
  const { api, isAdmin, me } = usePanel();
  const [list, setList] = useState([]);
  const [users, setUsers] = useState({ roles: [], users: [] });
  const [zipping, setZipping] = useState(false);
  const load = () => Promise.all([api.get("/admin/businesses"), api.get("/users")]).then(([b, u]) => { setList(b.data); setUsers(u.data); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isAdmin) return <Navigate to="/panel" replace />;

  const act = async (url, action) => { try { await api.post(url, { action }); toast.success("Güncellendi"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const setRole = async (uid, role) => { try { await api.patch(`/users/${uid}/role`, { role }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const downloadSource = async () => {
    setZipping(true);
    try {
      const res = await api.get("/admin/source-code", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "gazi-sepet-kaynak-kod.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Kaynak kod indirildi");
    } catch (e) { toast.error(errMsg(e)); } finally { setZipping(false); }
  };

  return (
    <div className="space-y-6 rise" data-testid="admin-page">
      <div><h1 className="text-3xl font-extrabold">Süper Yönetici</h1><p className="text-sm text-slate-500">Kayıtlı işletmelerin yıllık lisans (6.000 TL) ve platform aktivasyonlarını yönetin.</p></div>
      <div className="rounded-3xl bg-[#0F1F45] text-white p-6 flex flex-wrap items-center gap-5 justify-between" data-testid="admin-source-card">
        <div className="flex items-center gap-4">
          <FileArchive className="h-9 w-9 text-[#FF6B0A]" />
          <div>
            <p className="font-display font-bold text-lg">Gizli Kod Arşivi</p>
            <p className="text-sm text-white/60">Sitenin tüm kaynak kodu (ön yüz + sunucu). Bu bağlantıyı yalnızca süper yöneticiler görür.</p>
          </div>
        </div>
        <Button onClick={downloadSource} disabled={zipping} className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] gap-2" data-testid="admin-source-download-btn">
          <Download className="h-4 w-4" /> {zipping ? "Hazırlanıyor..." : "Kaynak Kodu İndir (ZIP)"}
        </Button>
      </div>
      <div className="space-y-3">
        {list.map((b) => (
          <div key={b.id} className="rounded-3xl bg-white border p-5 grid lg:grid-cols-[1fr_auto] gap-4" data-testid={`admin-business-${b.id}`}>
            <div><p className="font-display font-bold text-lg">{b.name}</p><p className="text-xs text-slate-500">{b.owner_email} • {b.phone} • Kayıt: {fmtDate(b.created_at)}</p></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-6"><span className="text-sm font-semibold">Panel lisansı</span>
                <Toggle active={b.license_active} until={b.license_until} testId={`admin-license-${b.id}`}
                  onOn={() => act(`/admin/businesses/${b.id}/license`, "activate")} onOff={() => act(`/admin/businesses/${b.id}/license`, "deactivate")} /></div>
              {Object.entries(PLATFORM_INFO).map(([k, p]) => (
                <div key={k} className="flex items-center justify-between gap-6"><span className="text-sm">{p.name}</span>
                  <Toggle active={b.platforms?.[k]?.active} until={b.platforms?.[k]?.active_until} testId={`admin-platform-${k}-${b.id}`}
                    onOn={() => act(`/admin/businesses/${b.id}/platform/${k}`, "activate")} onOff={() => act(`/admin/businesses/${b.id}/platform/${k}`, "deactivate")} /></div>
              ))}
            </div>
          </div>
        ))}
        {!list.length && <p className="text-slate-500">Kayıtlı işletme yok.</p>}
      </div>
      <div className="rounded-3xl bg-white border p-5">
        <h3 className="font-display font-bold mb-3">Kullanıcılar ve Roller</h3>
        <div className="divide-y">
          {users.users.map((u) => (
            <div key={u.user_id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`admin-user-${u.user_id}`}>
              <span className="flex-1 truncate">{u.email || u.user_id}</span>
              <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)} disabled={u.user_id === me.user.user_id}>
                <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{users.roles.map((r) => <SelectItem key={r} value={r}>{r === "admin" ? "Süper yönetici" : "İşletme"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
